import { TIMEOUT_MS } from "@/config/timeouts";
import { fetchJson } from "@/shared/http/fetch-json";
import { normalizeApiMessage } from "@/shared/helpers/errors";
import type { AuthUser, LoginRequestDto, LoginResult, SocialLoginRequestDto } from "../types/auth.dto";

/**
 * Ações de autenticação do cliente (login/social/logout) contra o BFF
 * /api/auth/*.
 *
 * Nenhum token vive no JavaScript. O access e o refresh ficam em cookies
 * httpOnly custodiados pelo servidor (route handlers + proxy /api/nest). Este
 * módulo só dispara as ações e devolve a identidade do usuário para a UI.
 */

/** Erro de auth já com mensagem pronta para exibir ao usuário. */
export class AuthError extends Error {
    constructor(
        message: string,
        readonly status: number
    ) {
        super(message);
        this.name = "AuthError";
    }
}

async function postAuth<T>(path: string, body?: unknown): Promise<T> {
    // Teto por chamada — backend pendurado degrada em vez de travar a UI.
    const { ok, status, data } = await fetchJson<T & { error?: unknown }>(
        path,
        {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        },
        TIMEOUT_MS.bffAuth
    );

    // status 0 = requisição não completou (rede/timeout).
    if (status === 0) {
        throw new AuthError("Não foi possível conectar. Verifique sua internet e tente novamente.", 0);
    }

    if (!ok) {
        // O BFF responde { error: string | string[] } — normaliza p/ exibição.
        throw new AuthError(
            normalizeApiMessage(data?.error, "Não foi possível completar a solicitação."),
            status
        );
    }

    return data as T;
}

/** POST /api/auth/login — grava access + refresh em cookies httpOnly; devolve só a identidade. */
export async function loginRequest(payload: LoginRequestDto): Promise<AuthUser> {
    const { user } = await postAuth<LoginResult>("/api/auth/login", payload);
    return user;
}

/** POST /api/auth/social — troca o idToken do Firebase por uma sessão (mesmo contrato do login). */
export async function socialLoginRequest(payload: SocialLoginRequestDto): Promise<AuthUser> {
    const { user } = await postAuth<LoginResult>("/api/auth/social", payload);
    return user;
}

/** POST /api/auth/logout — o handler lê o access do cookie, revoga no back e limpa os cookies. */
export async function logoutRequest(user: { cpfCnpj: string; email: string } | null): Promise<void> {
    await postAuth("/api/auth/logout", user ?? {});
}
